import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CREAM, RED, BLACK } from '../components/Layout';
import { matchesSearch } from '../search';

const SMARTSHOP_API = 'https://europe-west1-dbos-core-prod.cloudfunctions.net';

interface Product {
  gid: string;
  slug?: string;
  name: string;
  category: string;
  glen_price: number;
  primary_image: string;
  secondary_image: string;
  all_assets?: string[];
  stock_status: string;
  description: string;
  published: boolean;
}

const useImageCycle = (images: string[], active: boolean, interval = 2000) => {
  const [currentImg, setCurrentImg] = React.useState(0);
  const [zoomed, setZoomed] = React.useState(false);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  React.useEffect(() => {
    if (!active || images.length < 2) {
      if (active && images.length >= 1) setZoomed(true);
      return;
    }
    setZoomed(true);
    timerRef.current = setInterval(() => {
      setZoomed(false);
      setTimeout(() => {
        setCurrentImg(prev => (prev + 1) % images.length);
        setZoomed(true);
      }, 300);
    }, interval);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [active, images.length, interval]);

  React.useEffect(() => {
    if (!active) {
      if (timerRef.current) clearInterval(timerRef.current);
      setCurrentImg(0);
      setZoomed(false);
    }
  }, [active]);

  return { currentImg, zoomed };
};

const CATEGORY_MAP: Record<string, string[]> = {
  // Guitars
  'guitars': ['Acoustic Guitars', 'Electric Guitars', 'Classical Guitars', 'Bass Guitars', 'CigarBox Guitars'],
  'acoustic-guitars': ['Acoustic Guitars'],
  'electric-guitars': ['Electric Guitars'],
  'classical-guitars': ['Classical Guitars'],
  'bass-guitars': ['Bass Guitars'],
  'cigarbox-guitars': ['CigarBox Guitars'],
  // Ukuleles
  'ukuleles': ['Ukuleles'],
  // Traditional & Folk
  'traditional': ['Traditional & Folk', 'Mandolin', 'Banjo', 'Bouzouki', 'Concertina', 'Bodhran', 'Tin Whistles', 'Harps', 'Violins', 'Accordians'],
  'traditional-folk': ['Traditional & Folk', 'Mandolin', 'Banjo', 'Bouzouki', 'Concertina', 'Bodhran', 'Tin Whistles', 'Harps', 'Violins', 'Accordians'],
  'mandolin': ['Mandolin'],
  'banjo': ['Banjo'],
  'bouzouki': ['Bouzouki'],
  'concertina': ['Concertina'],
  'bodhran': ['Bodhran'],
  'tin-whistles': ['Tin Whistles'],
  'harps': ['Harps'],
  'violins': ['Violins'],
  'accordians': ['Accordians'],
  // Keyboards & Drums
  'keyboards': ['Keyboards & Pianos'],
  'drums': ['Drums'],
  // Strings — all subcategories
  'strings': ['Acoustic Strings', 'Electric Strings', 'Classical Strings', 'Bass Strings', 'Ukulele Strings', 'Mandolin Strings', 'Banjo Strings', 'Bouzouki Strings'],
  'acoustic-strings': ['Acoustic Strings'],
  'electric-strings': ['Electric Strings'],
  'classical-strings': ['Classical Strings'],
  'bass-strings': ['Bass Strings'],
  'ukulele-strings': ['Ukulele Strings'],
  'mandolin-strings': ['Mandolin Strings'],
  'banjo-strings': ['Banjo Strings'],
  'bouzouki-strings': ['Bouzouki Strings'],
  // Studio & Sound
  'studio-sound': ['Studio & Sound', 'Microphones', 'Speakers'],
  'studio': ['Studio & Sound'],
  'microphones': ['Microphones'],
  'speakers': ['Speakers'],
  // Other categories
  'music-books': ['Music Books'],
  'accessories': ['Accessories'],
  'pre-loved': ['Pre-Loved Gear'],
  'offers': ['Shop Offers'],
  'rental': ['Rental Equipment'],
  'rental-equipment': ['Rental Equipment'],
  'other': ['Other'],
  // Wind instruments (for future use)
  'wind': ['Flutes', 'Alto Sax', 'Clarinet', 'Recorders', 'Reeds'],
  'wind-instruments': ['Flutes', 'Alto Sax', 'Clarinet', 'Recorders', 'Reeds'],
  'flutes': ['Flutes'],
  'alto-sax': ['Alto Sax'],
  'clarinet': ['Clarinet'],
  'recorders': ['Recorders'],
  'reeds': ['Reeds'],
};

const CATEGORY_TITLES: Record<string, string> = {
  'studio-sound': 'Studio & Sound',
};

const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  const cardImages = [product.primary_image, product.secondary_image, ...(product.all_assets || [])].filter((img, i, arr) => !!img && arr.indexOf(img) === i).slice(0, 6);
  const { currentImg, zoomed } = useImageCycle(cardImages, hovered, 2000);

  return (
    <div onClick={() => navigate(`/product/${product.slug || product.gid}`)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className='product-glow' style={{ position: 'relative', overflow: 'hidden', background: 'white', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.08)' }}>
      <div style={{ position: 'relative', overflow: 'hidden', aspectRatio: '4/3', height: 'auto', minHeight: 180, maxHeight: 280 }}>
        {(hovered ? cardImages : cardImages.slice(0, 1)).map((img, i) => (
          <img key={i} src={img} alt={product.name} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center center', background: 'white',
            opacity: currentImg === i ? 1 : 0,
            transform: currentImg === i && zoomed ? 'scale(1.08)' : 'scale(1)',
            transition: 'opacity 0.3s ease-in-out, transform 2s ease-in-out',
            zIndex: currentImg === i ? 1 : 0,
          }} />
        ))}
        {product.stock_status === 'out_of_stock' && (
          <div style={{ position: 'absolute', top: 12, right: 12, background: BLACK, color: CREAM, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', padding: '4px 8px' }}>
            Out of Stock
          </div>
        )}
      </div>
      <div style={{ padding: '14px 16px 16px' }}>
        <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(0,0,0,0.4)', margin: '0 0 4px' }}>{product.category}</p>
        <p style={{ fontWeight: 700, fontSize: 13, color: BLACK, margin: '0 0 8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</p>
        <p style={{ fontWeight: 900, fontSize: 16, color: RED, margin: 0 }}>€{product.glen_price?.toFixed(2)}</p>
      </div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: RED }} />
    </div>
  );
};

export const ShopPage: React.FC<{ lang: string }> = ({ lang }) => {
  const { category } = useParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const navigate = useNavigate();

  const CATEGORIES: { label: string; icon: string }[] = [
    { label: 'Acoustic Guitars', icon: '🪕' },
    { label: 'Electric Guitars', icon: '🎸' },
    { label: 'Ukuleles', icon: '🪕' },
    { label: 'Traditional & Folk', icon: '🪗' },
    { label: 'Keyboards & Pianos', icon: '🎹' },
    { label: 'Drum Kit', icon: '🥁' },
    { label: 'Studio & Sound', icon: '🎙️' },
    { label: 'Strings', icon: '🎻' },
    { label: 'Bass Strings', icon: '🎻' },
    { label: 'Electric Strings', icon: '🎻' },
    { label: 'Tin Whistles', icon: '🪈' },
    { label: 'Music Books', icon: '📚' },
    { label: 'Accessories', icon: '🎧' },
    { label: 'Pre-Loved Gear', icon: '🔄' },
    { label: 'Shop Offers', icon: '📣' },
    { label: 'Other', icon: '🎚️' },
  ];

  // Sync activeFilter with URL category
  useEffect(() => {
    if (category && CATEGORY_MAP[category]) {
      setActiveFilter(category);
    } else if (!category) {
      setActiveFilter(null);
    }
  }, [category]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${SMARTSHOP_API}/smartshop-list-products`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ view: 'grid' }),
        });
        const data = await res.json();
        let prods = (data.products || []).map((p: any) => ({
          gid: p.gid, slug: p.slug, name: p.name, category: p.category,
          glen_price: p.price, primary_image: p.primary_image, secondary_image: p.secondary_image,
          all_assets: p.all_assets, stock_status: p.stock_status, description: p.description,
          published: p.status === 'active',
        } as Product));
        // No client-side sort - see App.tsx. This page grouped by category and
        // then name, which read tidily and was still a second opinion about an
        // order the shop already decided. Newest first, everywhere.
        setProducts(prods);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []); // load ALL products once - filtering happens in render, no refetch, no race

  // The search term lives in the URL (?q=), not only in state. A browser restores a
  // history entry's URL, never a component's state - so with the term in state alone,
  // searching "korala", opening a product and pressing Back landed on an empty shop and
  // the shopper had to type it again.
  //
  // replaceState rather than pushState: typing must not push a history entry per
  // keystroke, but it does leave ?q= on the entry we navigate AWAY from, which is
  // exactly what Back returns to. Side benefit: a search result is now a shareable link.
  const [searchTerm, setSearchTerm] = useState(
    () => new URLSearchParams(window.location.search).get('q') || ''
  );

  useEffect(() => {
    const url = new URL(window.location.href);
    if (searchTerm.trim()) url.searchParams.set('q', searchTerm);
    else url.searchParams.delete('q');
    window.history.replaceState(null, '', url.toString());
  }, [searchTerm]);
    // --- grid pagination (Phase-3 SmartShop standard: callback-ref IntersectionObserver) ---
  // Render a page of cards, grow as the sentinel scrolls into view. A callback ref
  // (not useRef+effect) attaches the observer on the node's first mount, so it works
  // on first load, not only after re-navigating.
  // PAGE is 8, not 24: each card costs one image request, and Glen's migrated originals
  // have no renditions, so 24 cards put 24 full-size images in flight in the same tick.
  // rootMargin must stay well under one page of cards - at 600px the sentinel sat inside
  // the band after an 8-card page, re-fired immediately and cascaded back to 24.
  const PAGE = 8;
  const [visibleCount, setVisibleCount] = useState(PAGE);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (node) {
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) setVisibleCount((n) => n + PAGE);
      }, { rootMargin: '200px' });
      observerRef.current.observe(node);
    }
  }, []);
  // reset to first page whenever the filter or search changes
  useEffect(() => { setVisibleCount(PAGE); }, [activeFilter, searchTerm]);
  const filteredByCategory = activeFilter
    ? products.filter((p: Product) => (CATEGORY_MAP[activeFilter] || [activeFilter]).includes(p.category))
    : products;
  const displayedProducts = searchTerm.trim()
    ? filteredByCategory.filter((p: Product) => matchesSearch(p, searchTerm))
    : filteredByCategory;

  return (
    <div style={{ padding: '0 40px 64px', marginTop: 32, background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(139,26,26,0.06) 0%, rgba(244,241,225,0) 60%), #F4F1E1', minHeight: '70vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <h1 style={{ fontWeight: 900, fontStyle: 'italic', fontSize: 'clamp(1.8rem,4vw,3rem)', color: BLACK, margin: 0 }}>
          {category ? (CATEGORY_MAP[category] ? (CATEGORY_TITLES[category] || category.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')) : 'Shop') : 'All Instruments'}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <input type="text" placeholder="Search by name or category" value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)', padding: '9px 16px', fontSize: 13, outline: 'none', width: 220, fontFamily: 'inherit' }} />
          <p style={{ fontSize: 11, color: 'rgba(0,0,0,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0, whiteSpace: 'nowrap' }}>
            {displayedProducts.length} {displayedProducts.length === 1 ? 'product' : 'products'}
          </p>
        </div>
      </div>
      {loading ? (
        <p style={{ fontSize: 11, textTransform: 'uppercase', color: 'rgba(0,0,0,0.3)', padding: '40px 0' }}>Loading...</p>
      ) : displayedProducts.length === 0 ? (
        <div style={{ padding: '60px 0', textAlign: 'center' }}>
          <p style={{ fontSize: 14, color: 'rgba(0,0,0,0.4)', marginBottom: 20 }}>No products found.</p>
          <button onClick={() => { navigate('/shop'); setSearchTerm(''); }}
            style={{ background: RED, color: 'white', border: 'none', padding: '12px 24px', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' }}>
            View All Products
          </button>
        </div>
      ) : (
        <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
          {displayedProducts.slice(0, visibleCount).map((p: Product) => <ProductCard key={p.gid} product={p} />)}
        </div>
        {visibleCount < displayedProducts.length && (
          // key forces a remount on each growth: IntersectionObserver only reports a
          // threshold crossing, so a sentinel that stays inside the band is never
          // reported again. Remounting re-observes it and yields a fresh entry.
          <div key={visibleCount} ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
        )}
        </>
      )}
    </div>
  );
};

export default ShopPage;
